import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';
import QuestionEditorModal from './QuestionEditorModal';

/**
 * Lists all questions for a course and provides edit/delete actions.
 *
 * The rich-HTML question body is rendered as sanitized-ish HTML so that math
 * formulas, sub/superscript, and diagrams preview in-place. (Quill output is
 * produced by our own editor so we treat it as trusted-but-constrained.)
 */
function CourseQuestionsModal({
  tenantId,
  course,
  segments,
  enabledAttributes,
  onClose,
  onChanged,
}) {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editQuestionId, setEditQuestionId] = useState(null);

  const loadQuestions = useCallback(async () => {
    if (!tenantId || !course) return;
    try {
      setLoading(true);
      const res = await api.get(
        `/question-bank/questions/${tenantId}/${course.CourseID}`
      );
      if (res.data.success) setQuestions(res.data.data || []);
    } catch {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [tenantId, course]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleAdd = () => {
    setEditQuestionId(null);
    setEditorOpen(true);
  };

  const handleEdit = (q) => {
    setEditQuestionId(q.QuestionID);
    setEditorOpen(true);
  };

  const handleDelete = async (q) => {
    const preview = (q.QuestionPlain || '').slice(0, 60) || `question #${q.QuestionID}`;
    if (!(await confirmDelete(`"${preview}${preview.length >= 60 ? '…' : ''}"`))) return;
    try {
      await api.delete(`/question-bank/questions/${q.QuestionID}`);
      toast.success('Question deleted');
      await loadQuestions();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete question');
    }
  };

  const handleToggleActive = async (q) => {
    // Optimistic flip — revert on failure.
    const next = !q.IsActive;
    setQuestions((list) =>
      list.map((x) => (x.QuestionID === q.QuestionID ? { ...x, IsActive: next } : x))
    );
    try {
      await api.put(`/question-bank/questions/${q.QuestionID}`, {
        courseId: course.CourseID,
        segmentId: q.SegmentID,
        questionHtml: q.QuestionHtml,
        marks: q.Marks,
        co: q.CO,
        unit: q.Unit,
        bl: q.BL,
        type: q.Type,
        difficulty: q.Difficulty,
        kp: q.KP,
        po: q.PO,
        isActive: next,
        tenantId,
      });
      onChanged && onChanged();
    } catch {
      toast.error('Failed to update question');
      setQuestions((list) =>
        list.map((x) =>
          x.QuestionID === q.QuestionID ? { ...x, IsActive: !next } : x
        )
      );
    }
  };

  const renderAttributeBadges = (q) => {
    const chips = [];
    if (enabledAttributes.includes('co') && q.CO) chips.push({ k: 'CO', v: q.CO });
    if (enabledAttributes.includes('unit') && q.Unit) chips.push({ k: 'Unit', v: q.Unit });
    if (enabledAttributes.includes('bl') && q.BL) chips.push({ k: 'BL', v: q.BL });
    if (enabledAttributes.includes('type') && q.Type) chips.push({ k: 'Type', v: q.Type });
    if (enabledAttributes.includes('difficulty') && q.Difficulty) chips.push({ k: 'Level', v: q.Difficulty });
    if (enabledAttributes.includes('kp') && q.KP) chips.push({ k: 'KP', v: q.KP });
    if (enabledAttributes.includes('po') && q.PO) chips.push({ k: 'PO', v: q.PO });
    if (chips.length === 0) return null;
    return (
      <div className="qb-question-chips">
        {chips.map((c) => (
          <span key={c.k} className="qb-question-chip">
            <span className="qb-question-chip-label">{c.k}</span>
            <span className="qb-question-chip-value">{c.v}</span>
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content qb-questions-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 style={{ margin: 0 }}>
              Questions — {course?.CourseCode} · {course?.CourseName}
            </h2>
            <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 2 }}>
              {questions.length}{' '}
              {questions.length === 1 ? 'question' : 'questions'} in this course
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-add" onClick={handleAdd}>
              + Add Question
            </button>
            <button className="btn btn-close" onClick={onClose}>×</button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
            Loading questions…
          </div>
        ) : questions.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9e9e9e' }}>
            No questions yet. Click <strong>+ Add Question</strong> to get
            started, or use <em>Import</em> from the Question Bank page to
            bulk-upload.
          </div>
        ) : (
          <div className="modal-body qb-questions-list">
            {questions.map((q, idx) => (
              <div
                key={q.QuestionID}
                className={`qb-question-row ${!q.IsActive ? 'qb-question-row-inactive' : ''}`}
              >
                <div className="qb-question-row-head">
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span className="qb-question-index">#{idx + 1}</span>
                    <span className="qb-question-segment">{q.SegmentName}</span>
                    <span className="qb-question-marks">{q.Marks} marks</span>
                    {!q.IsActive && (
                      <span className="qb-question-inactive-tag">Inactive</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <label
                      className="toggle-switch toggle-switch-sm"
                      title={q.IsActive ? 'Deactivate' : 'Activate'}
                    >
                      <input
                        type="checkbox"
                        checked={!!q.IsActive}
                        onChange={() => handleToggleActive(q)}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <button
                      className="action-btn action-edit"
                      title="Edit"
                      onClick={() => handleEdit(q)}
                    >
                      ✏
                    </button>
                    <button
                      className="action-btn action-delete"
                      title="Delete"
                      onClick={() => handleDelete(q)}
                    >
                      🗑
                    </button>
                  </div>
                </div>
                <div
                  className="qb-question-body"
                  // Quill HTML is produced by our own editor; fine to render inline.
                  dangerouslySetInnerHTML={{ __html: q.QuestionHtml || '' }}
                />
                {renderAttributeBadges(q)}
              </div>
            ))}
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-cancel" onClick={onClose}>Close</button>
        </div>

        {editorOpen && (
          <QuestionEditorModal
            tenantId={tenantId}
            questionId={editQuestionId}
            defaultCourseId={course.CourseID}
            courses={[course]}
            segments={segments}
            enabledAttributes={enabledAttributes}
            onClose={() => setEditorOpen(false)}
            onSaved={async () => {
              setEditorOpen(false);
              await loadQuestions();
              onChanged && onChanged();
            }}
          />
        )}
      </div>
    </div>
  );
}

export default CourseQuestionsModal;
